/**
 * PreviewHorizontalCard Component
 *
 * Custom horizontal card for preview that handles R2 image URLs.
 * Uses SubmissionImage for proper presigned URL fetching.
 *
 * Features:
 * - Uses horizontalTileUrl if available, falls back to backdropUrl
 * - Handles R2 keys via SubmissionImage component
 * - Shows metadata (title, year, runtime, genres, director)
 * - Action buttons (Bookmark and Info) displayed but disabled
 */

import type { ReactElement } from "react";
import { Button } from "@lumi/ui-headless";
import { Bookmark, Info, Play } from "lucide-react";
import { useCascadeFadeIn } from "../../../../shared/hooks";
import { SubmissionImage } from "../../components/SubmissionImage";
import type { TitlePreviewData } from "./TitlePreview";

interface PreviewHorizontalCardProps {
  readonly data: TitlePreviewData;
  readonly submissionId?: string | null;
  readonly titleId?: string | null;
  readonly index?: number;
}

/**
 * Card metadata section with title, year, runtime, genres, and director
 */
function CardMetadata({ data }: { readonly data: TitlePreviewData }): ReactElement {
  return (
    <div className="px-1 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <h2 className="font-medium text-white text-base leading-tight line-clamp-2">{data.title}</h2>

          <div className="space-y-1">
            {/* Year, Runtime, and Genres */}
            <div className="flex items-center gap-2 text-xs text-neutral-400 min-w-0">
              {data.year && <span className="flex-shrink-0">{data.year}</span>}
              {data.year && data.runtime && <span className="flex-shrink-0">•</span>}
              {data.runtime && <span className="flex-shrink-0">{data.runtime} min</span>}
              {(data.year || data.runtime) && data.genres && data.genres.length > 0 && (
                <span className="flex-shrink-0">•</span>
              )}
              {data.genres && data.genres.length > 0 && (
                <span className="truncate">{data.genres.slice(0, 2).join(", ")}</span>
              )}
            </div>

            {/* Director */}
            {data.director && (
              <p className="text-xs text-neutral-500 truncate">
                Dir. <span className="text-neutral-400 font-medium">{data.director}</span>
              </p>
            )}
          </div>
        </div>

        {/* Action buttons - displayed but disabled */}
        <div className="flex items-center gap-2 pointer-events-none">
          <Button
            variant="ghost-sm"
            className="pl-0 flex-shrink-0 pointer-events-auto"
            onClick={() => {}}
            disabled={true}
            aria-label="Add to watchlist"
          >
            <Bookmark size={16} fill="none" />
          </Button>
          <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={() => {}}>
            <Info size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function PreviewHorizontalCard({
  data,
  submissionId,
  titleId,
  index = 0,
}: PreviewHorizontalCardProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  // Determine which image to use - prefer horizontal tile, fallback to backdrop
  const imageUrl = data.horizontalTileUrl ?? data.backdropUrl;

  return (
    <div
      ref={ref}
      className={`
                group flex-shrink-0 self-start text-left w-[calc(100vw-3rem)] sm:w-[500px] space-y-3 snap-center
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
            `}
      style={{
        transition: "opacity 500ms ease-out, transform 500ms ease-out",
      }}
    >
      {/* Landscape image with 16:9 aspect ratio */}
      <div className="card-base card-hover-horizontal aspect-video">
        {/* Backdrop/tile image via SubmissionImage for R2 support */}
        {imageUrl && (
          <SubmissionImage
            src={imageUrl}
            alt={data.title}
            className="h-full w-full object-cover"
            submissionId={submissionId}
            titleId={titleId}
          />
        )}

        {/* Diagonal gradient overlays on hover - dark from bottom-left, light from top-right */}
        {/* No rotation/scaling on touch devices to prevent overflow */}
        <div className="card-gradient-light" />
        <div className="card-gradient-dark" />

        {/* Play button overlay - shown on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity delay-100 duration-200 ease-out z-10 pointer-events-none">
          <Button variant="ghost-sm" className="pointer-events-auto" onClick={() => {}}>
            <Play size={20} />
            <span className="ml-2 text-base font-semibold">Play</span>
          </Button>
        </div>
      </div>

      {/* Metadata below the card */}
      <CardMetadata data={data} />
    </div>
  );
}
